import * as React from 'react'
import { t } from 'ttag'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { StartResetPassword$Request } from '../../services/api.types'
import YumBack from '../../components/YumBack/YumBack'
import Typography from '@material-ui/core/Typography'
import InputAdornment from '@material-ui/core/InputAdornment'
import YumTextField from '../../components/YumTextField/YumTextField'
import { ReactComponent as EmailIcon } from '../../assets/v2/icons/email.svg'
import { ReactComponent as EmailFilledIcon } from '../../assets/v2/icons/email-filled.svg'
import YumButton from '../../components/YumButton/YumButton'
import { ReactComponent as ErrorIcon } from '../../assets/v2/icons/error.svg'
import { isEmailFieldErrorVisible } from '../../common/helpers/formHelpers'
import { Tooltip } from '@material-ui/core'
import { ReactComponent as EmailErrorIcon } from '../../assets/v2/icons/email-error.svg'
import { INotification } from '../../common/notifications/NotificationReducers'
import { store } from '../../index'
import { addNotification } from '../../common/notifications/NotificationActions'
import { IGlobalState } from '../../Root/store/appReducer'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import { resetForgotPasswordState } from './store/ForgotPasswordActions'

export interface IForgotPasswordProps {
  startResetPassword(payload: StartResetPassword$Request): void
  submitting: boolean
}

export interface IForgotPasswordState {
  email: string
  isFormValid: boolean
  isFormSubmitted: boolean
  showPassword: boolean
  errors: object
  focus: object
  [key: string]: any
}

export class ForgotPassword extends React.Component<
  IForgotPasswordProps & { location?: { state?: { email?: string } } },
  IForgotPasswordState
> {
  state = {
    // Pre-fill with the email passed from the login screen's "Forgot password?" link.
    email: this.props.location?.state?.email || '',
    isFormValid: true,
    isFormSubmitted: false,
    showPassword: false,
    errors: {
      email: false,
      password: false,
    },
    focus: {
      email: false,
      password: false,
    },
  }

  componentDidMount() {
    // Clear any submitting state left over from a previous visit (e.g. after a successful
    // submit navigated away while submitting was still true).
    store.dispatch(resetForgotPasswordState())
    const globalState = store.getState() as any as IGlobalState
    // @ts-ignore
    if (globalState.router && globalState.router.location && globalState.router.location.query) {
      // @ts-ignore
      const query = globalState.router.location.query
      if (query && query.error && query.error === 'linkExpired') {
        const notification: INotification = {
          type: 'warning',
          content: t`Link expired. Insert your e-mail and try again!`,
        }
        store.dispatch(addNotification(notification))
      }
    }
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    this.setState({ [name]: value })
  }

  handleFocus = (name: string) => {
    this.setState({ focus: { ...this.state.focus, [name]: true } })
  }

  handleBlur = (name: string) => {
    this.setState({ focus: { ...this.state.focus, [name]: false } })
  }

  handleKeyPress = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13) {
      // @ts-ignore
      event.target.blur()
      await this.onSubmit()
    }
  }

  validateEmailField = async (value: string) => {
    const error = isEmailFieldErrorVisible(value)
    await this.setState({
      errors: { ...this.state.errors, email: error },
    })
    await this.setState({
      isFormValid: this.isFormValid(),
    })
  }

  isFormValid = () => !Object.values(this.state.errors).some(Boolean)

  onSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault()
    }
    if (this.props.submitting) return
    await this.setState({ isFormSubmitted: true })

    await this.validateEmailField(this.state.email)

    if (this.state.isFormValid) {
      await this.props.startResetPassword(this.state)
    }
  }

  render() {
    const emailNotEmpty = !!this.state.email

    const title = t`Recover your
    YuMuuv account`
    return (
      <Card className="login pt-12">
        <div className="login__inner">
          <YumBack to="/login" />
          <NotificationsWrapper />
          <form className="login__form" onSubmit={this.onSubmit} noValidate>
            <Typography className={'h1-desktop'} variant="h1">
              {title}
            </Typography>

            <YumTextField
              placeholder={t`Email`}
              type="email"
              name="email"
              value={this.state.email}
              onChange={this.handleInputChange}
              onKeyDown={this.handleKeyPress}
              error={this.state.errors.email}
              onFocus={() => this.handleFocus('email')}
              onBlur={() => this.handleBlur('email')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {this.state.errors.email && !this.state.focus.email ? (
                      <EmailErrorIcon />
                    ) : emailNotEmpty || this.state.focus.email ? (
                      <EmailFilledIcon />
                    ) : (
                      <EmailIcon />
                    )}
                  </InputAdornment>
                ),
                endAdornment: this.state.errors.email ? (
                  <InputAdornment position="end">
                    <Tooltip
                      title={t`Something is wrong with this email`}
                      placement="top-end"
                      open={emailNotEmpty && !this.state.focus.email}
                      arrow
                    >
                      <ErrorIcon />
                    </Tooltip>
                  </InputAdornment>
                ) : (
                  ''
                ),
              }}
            />

            <YumButton type="submit" showLoader={true} disabled={this.props.submitting}>
              {t`Reset`}
            </YumButton>
          </form>
        </div>
        {/*<div className="login__sidebar" />*/}
        <DesktopSidebar />
      </Card>
    )
  }
}
